import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import QCBundleCard from './QCBundleCard';
import QCBundleDetail from './QCBundleDetail';
import QCFailModal from './QCFailModal';
import QCActionBar from './QCActionBar';
import { buildFontFaceCss } from './NumberPreviewThumb';
import {
    buildBundles,
    computeBundleStates,
    getAttr,
    getGroupPlayerName,
    getGroupTeamName,
    EMBELLISHMENT_TYPES,
    augmentBundleSiblings,
} from './pressingHelpers';
import { authenticatedFetch } from './AuthComponent';
import { getPreviewImageUrls } from './previewImage';
import { playSuccess, playError } from './sounds';
import ManagerOverrideModal from './ManagerOverrideModal';
import ManagerOverrideBanner from './ManagerOverrideBanner';
import CameraScanner from './CameraScanner';
import { MANAGER_BARCODE_RX } from './PressingOrderBoard';
import {
    DndContext,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    useDraggable,
    useDroppable,
} from '@dnd-kit/core';
import { useT, tImperative } from './i18n/I18nContext';
import VirtualCardList from './VirtualCardList';

const VIRTUAL_TITLES = new Set(['free returns + package protection', 'processing fee']);
function isVirtual(title) {
    return VIRTUAL_TITLES.has((title || '').toLowerCase()) || /^(Tip|Gift card|Shipping|Tax)/i.test(title || '');
}

// Strip !Player#N / #N / |suffix suffixes to get the base SKU for require_scanning lookup.
// Mirrors the server-side regex: re.sub(r'[!#|].*$', '', group_key)
function getBaseSku(sku) {
    if (!sku) return '';
    const m = sku.search(/[!#|]/);
    return m === -1 ? sku : sku.slice(0, m);
}

function getPackingUnits(order, shopifyLocationId) {
    if (!order) return { units: [], groupsByGid: {}, virtualFoLineItems: [] };

    const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node);
    // Strict-match by location for packer sessions (which always carry a
    // shopifyLocationId). When empty/null — admin viewing directly without
    // emulating — fall back to "all OPEN/IN_PROGRESS FOs".
    const locGid = shopifyLocationId ? `gid://shopify/Location/${shopifyLocationId}` : null;
    const openFoLineItems = {};
    for (const fo of fos) {
        const assignedId = fo.assignedLocation?.location?.id || '';
        if (locGid && assignedId !== locGid) continue;
        if (!['OPEN', 'IN_PROGRESS'].includes(fo.status)) continue;
        // Slip already printed for this FO here (franchise split remainder
        // case: the packed FO stays at the same location) — not work anymore.
        if (fo._packedHere) continue;
        for (const e of (fo.lineItems?.edges || [])) {
            if ((e.node.remainingQuantity || 0) > 0) {
                openFoLineItems[e.node.lineItem?.id] = e.node;
            }
        }
    }
    // Per-LI remaining qty across all OPEN/IN_PROGRESS FOs (used by
    // augmentBundleSiblings to scale Shopify-misreported children).
    const remainingByLiId = {};
    for (const [liId, foLi] of Object.entries(openFoLineItems)) {
        remainingByLiId[liId] = (remainingByLiId[liId] || 0) + (foLi.remainingQuantity || 0);
    }

    const allLineItems = (order.lineItems?.edges || []).map(e => e.node);
    const allLineItemsById = {};
    for (const li of allLineItems) allLineItemsById[li.id] = li;

    // Find FOs that contain ONLY virtual items — these get fulfilled alongside the main FO
    const virtualFoIds = [];
    for (const fo of fos) {
        const assignedId = fo.assignedLocation?.location?.id || '';
        if (locGid && assignedId !== locGid) continue;
        if (!['OPEN', 'IN_PROGRESS'].includes(fo.status)) continue;
        // Slip already printed for this FO here (franchise split remainder
        // case: the packed FO stays at the same location) — not work anymore.
        if (fo._packedHere) continue;
        const foLineItems = (fo.lineItems?.edges || []).map(e => e.node);
        const allVirtual = foLineItems.every(fli => {
            const li = allLineItemsById[fli.lineItem?.id];
            return li && isVirtual(li.title);
        });
        if (allVirtual && foLineItems.length > 0) virtualFoIds.push(fo.id);
    }

    // Business rule: "Juventus Camps" orders ship as a legacy Shopify bundle
    // (lineItemGroup with null variantSku) which otherwise collapses into one
    // QC card. Strip lineItemGroup so each item QCs individually.
    const LEGACY_BUNDLE_TAGS = new Set(['Juventus Camps']);
    const splitLegacyBundle = (order?.tags || []).some(t => LEGACY_BUNDLE_TAGS.has(t));

    // Strict match: only items in OPEN/IN_PROGRESS FOs at the user's scoped
    // location. Augment with bundle siblings whose Shopify remainingQuantity is
    // 0 (mis-allocation in earlier waves) so orphan bundles re-appear in QC
    // with their embellishments intact. Children are flagged
    // `_shopifyMisreportedShipped` so QCBundleCard can paint a warning chip.
    const augmentSource = allLineItems
        .filter(li => !isVirtual(li.title))
        .filter(li => (li.currentQuantity ?? li.quantity ?? 0) > 0);
    const relevantRaw = augmentBundleSiblings(
        augmentSource.filter(li => openFoLineItems[li.id]),
        augmentSource,
        remainingByLiId
    );
    const relevant = splitLegacyBundle
        ? relevantRaw.map(li => ({ ...li, lineItemGroup: null }))
        : relevantRaw;

    const bundles = buildBundles(relevant, { tags: order?.tags });
    const groups = bundles.map(b => ({
        gid: b.gid,
        items: [b.parent, ...b.children],
        groupData: b.groupData,
    }));

    const groupsByGid = {};
    for (const g of groups) groupsByGid[g.gid] = g;

    // Order-level player→number fallback. Some carts only put "Player Name" on
    // the line item — the number lives on the _soccer_order_info attribute.
    // Build a name→number map so the QC card can show "#50" next to "Kai Slocum"
    // even when no Player Number attr was attached to the line item.
    const playerNumberByName = {};
    try {
        const raw = (order?.customAttributes || []).find(a => a.key === '_soccer_order_info')?.value;
        if (raw) {
            for (const p of (JSON.parse(raw) || [])) {
                const nm = p?.name;
                // Broken-order team-bulk: assigned_number array trumps singular config.number.
                const assigned = p?.config?.assigned_number;
                if (Array.isArray(assigned) && assigned.length > 0) {
                    const nums = assigned.map(n => String(n).trim()).filter(Boolean);
                    if (nm && nums.length) {
                        try { nums.sort((a, b) => parseInt(a, 10) - parseInt(b, 10)); } catch (_) {}
                        playerNumberByName[nm] = nums.join(', ');
                        continue;
                    }
                }
                const num = p?.config?.number ?? p?.number;
                if (nm && num != null && String(num).trim() !== '') {
                    playerNumberByName[nm] = String(num).trim();
                }
            }
        }
    } catch (_) { /* malformed JSON → no fallback */ }

    const units = [];
    // instanceIndex must be unique per group_key ACROSS all bundles — otherwise
    // two line items with the same SKU (qty 1 each) both get instanceIndex=1,
    // and pack_instances keys collide (marking one unit persists onto both).
    const instanceCountByGroupKey = {};
    for (const g of groups) {
        const isBundle = g.items.some(li => EMBELLISHMENT_TYPES.has(li.productType)) &&
                         g.items.some(li => !EMBELLISHMENT_TYPES.has(li.productType));
        const parent = g.items.find(li => li.sku === g.groupData?.variantSku)
            || g.items.find(li => !EMBELLISHMENT_TYPES.has(li.productType))
            || g.items[0];
        // Use the OPEN FO's remainingQuantity so already-fulfilled units (e.g. 1 of
        // 2 identical socks already shipped) don't reappear on the board. Falls
        // back to the full line-item quantity when no FO membership is available
        // (e.g. admin sidebar view with no location filter).
        const parentRemaining = openFoLineItems[parent?.id]?.remainingQuantity;
        const qty = (typeof parentRemaining === 'number' && parentRemaining >= 0)
            ? parentRemaining
            : (parent?.quantity || 1);
        if (qty < 1) continue;

        const groupAttrs   = g.groupData?.customAttributes || [];
        const allItemAttrs = g.items.flatMap(li => li.customAttributes || []);
        const playerName   = getAttr(groupAttrs, 'Player Name', 'player_name')
            || getAttr(allItemAttrs, 'Player Name', 'player_name');
        const playerNumber = getAttr(groupAttrs, 'Player Number', 'player_number', 'Number', '#')
            || getAttr(allItemAttrs, 'Player Number', 'player_number', 'Number', '#')
            || (playerName ? playerNumberByName[playerName] : '')
            || '';

        const foLineItemIds = g.items
            .map(li => openFoLineItems[li.id]?.id)
            .filter(Boolean);
        const foLineItemRefs = g.items
            .map(li => openFoLineItems[li.id]?.id ? { id: openFoLineItems[li.id].id, orderLineItemId: li.id } : null)
            .filter(Boolean);

        // No-SKU parents (e.g. TP replacement/draft orders drop SKUs) must use
        // item_state's group-key convention — a bare gid key would upsert a
        // phantom row that the next item_state refresh deletes, silently
        // losing the QC state (SP-355545:A pants bug, 2026-08-26).
        const group_key = parent?.sku || ('__no_sku__' + (parent?.id || g.gid));

        const allAttrs = [...groupAttrs, ...allItemAttrs];
        const { front: previewFront, back: previewBack } = getPreviewImageUrls(allAttrs);

        const bundleRecovery = g.items.some(li => li._shopifyMisreportedShipped);
        for (let i = 0; i < qty; i++) {
            instanceCountByGroupKey[group_key] = (instanceCountByGroupKey[group_key] || 0) + 1;
            units.push({
                unitKey:        `${g.gid}__${i}`,
                group_key,
                gid:            g.gid,
                title:          parent?.title || tImperative('Item'),
                productType:    parent?.productType || '',
                variantTitle:   parent?.variant?.title && parent.variant.title !== 'Default Title' ? parent.variant.title : null,
                sku:            parent?.sku || null,
                imageUrl:       parent?.image?.url || null,
                previewFront,
                previewBack,
                playerName,
                playerNumber,
                isBundle,
                instanceIndex:  instanceCountByGroupKey[group_key],
                totalInstances: qty,
                foLineItemIds,
                foLineItemRefs,
                bundleRecovery,
            });
        }
    }
    return { units, groupsByGid, virtualFoIds };
}

// Height estimator for the virtualized kanban columns. Since .qcb-card has
// `min-height: 320px` in CSS and the garment-name is 2-line-clamped, every
// card is exactly 320px. Flat estimate matches actual so measureElement is
// a no-op and cards never overlap. Keep in sync with the .qcb-card
// min-height in styles.css.
function qcCardHeightFor(_unit) {
    return 320;
}

function DraggableCard({ unitKey, unit, pickStatus, failReason, onClick, scanOnly, overrideActive, skuNotification, tpOverridesByName }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: unitKey });
    return (
        <QCBundleCard
            unit={unit}
            pickStatus={pickStatus}
            failReason={failReason}
            onClick={onClick}
            scanOnly={scanOnly}
            overrideActive={overrideActive}
            dragRef={setNodeRef}
            dragAttrs={attributes}
            dragListeners={listeners}
            isDragging={isDragging}
            skuNotification={skuNotification}
            tpOverridesByName={tpOverridesByName}
        />
    );
}

// Opens the per-order rework slip in a new tab. The slip endpoint auto-prints
// on load and the tab self-closes after the print dialog dismisses (same
// mechanic the packing slip uses).
//
// Pass the active session so the slip credits the PIN user (e.g. the QC
// kiosk operator) instead of the admin JWT the tab is signed into.
// Without `qc_name=`, the backend falls back to the JWT user → every slip
// printed from a shared kiosk reads as the kiosk's admin owner.
function openReworkSlip({ orderId, fulfillmentOrderId, storeKey, session }) {
    if (!orderId || !fulfillmentOrderId) return;
    const params = new URLSearchParams({ store: storeKey, fulfillment_order_id: fulfillmentOrderId });
    const presserName = session
        ? [session.first_name, session.last_name].filter(Boolean).join(' ').trim()
        : '';
    if (presserName) params.set('qc_name', presserName);
    if (session?.presser_id) params.set('portal_userid', String(session.presser_id));
    window.open(`/api/orders/${orderId}/rework-slip-html?${params.toString()}`, '_blank', 'noopener');
}

// Each kanban column has a header, an optional top addon (the scan input on
// READY), and a virtualized card list. Cards mount lazily as the column
// scrolls so giant orders (hundreds of cards) don't freeze the iPad.
function DroppableColumn({
    id, className, title, count,
    topAddon, items, getKey, renderItem,
    estimateSize = 220,
}) {
    const { setNodeRef, isOver } = useDroppable({ id });
    return (
        <div ref={setNodeRef}
             className={`${className}${isOver ? ' qcb__col--over' : ''}`}>
            <div className="qcb__col-hdr">
                <span className="qcb__col-title">{title}</span>
                <span className="qcb__col-count">{count}</span>
            </div>
            {topAddon}
            <VirtualCardList
                className="qcb__cards"
                items={items}
                getKey={getKey}
                renderItem={renderItem}
                estimateSize={estimateSize}
                overscan={6}
                gap={12}
            />
        </div>
    );
}

export default function QCBoard({
    orderResult, session, onExit, fetchFn = authenticatedFetch,
    notifications = [], onReviewNotifications = null,   // 2026-09-02: header "Review Notification" button
}) {
    const t = useT();
    const order = orderResult.order;
    const orderId = order.id?.split('/').pop() || '';
    const savedPackState = orderResult.pack_state || {};

    // SKU Tools: {sku: message} for red badge on QCBundleCard tiles.
    const [skuNotifications, setSkuNotifications] = useState({});
    useEffect(() => {
        if (!orderId) return;
        fetchFn(`/api/sku-notifications/for-order/${orderId}`)
            .then(r => r.ok ? r.json() : { sku_notifications: {} })
            .then(j => setSkuNotifications(j.sku_notifications || {}))
            .catch(() => setSkuNotifications({}));
    }, [orderId, fetchFn]);

    const { units, groupsByGid, virtualFoIds } = useMemo(
        () => getPackingUnits(order, session.shopify_location_id),
        [order, session.shopify_location_id]
    );

    // item_state now keeps a per-instance map (pack_instances) so 1 pass + 1 fail
    // on a duplicate-SKU order survives close/reopen. Fallback for legacy rows
    // (empty/missing pack_instances but a pack_status set): apply the status to
    // the first unit only (lowest instanceIndex), rest default to ready.
    const unitsByGroup = useMemo(() => {
        const m = {};
        for (const u of units) (m[u.group_key] ||= []).push(u);
        for (const gk of Object.keys(m)) {
            m[gk].sort((a, b) => (a.instanceIndex || 0) - (b.instanceIndex || 0));
        }
        return m;
    }, [units]);

    const statusToColumn = (s) => (s === 'passed' ? 'passed' : s === 'failed' ? 'rework' : 'ready');

    const [columnByKey, setColumnByKey] = useState(() => {
        const init = {};
        for (const u of units) init[u.unitKey] = 'ready';
        for (const [gk, group] of Object.entries(unitsByGroup)) {
            const ps = savedPackState[gk];
            if (!ps) continue;
            const instMap = ps.pack_instances || {};
            const hasInstanceData = Object.keys(instMap).length > 0;
            if (hasInstanceData) {
                for (const u of group) {
                    const entry = instMap[String(u.instanceIndex)];
                    if (entry?.status) init[u.unitKey] = statusToColumn(entry.status);
                }
            } else {
                const first = group[0];
                if (!first) continue;
                if (ps.pack_status === 'passed') init[first.unitKey] = 'passed';
                else if (ps.pack_status === 'failed') init[first.unitKey] = 'rework';
            }
        }
        return init;
    });

    const [failState, setFailState] = useState(() => {
        const init = {};
        for (const [gk, group] of Object.entries(unitsByGroup)) {
            const ps = savedPackState[gk];
            if (!ps) continue;
            const instMap = ps.pack_instances || {};
            const hasInstanceData = Object.keys(instMap).length > 0;
            if (hasInstanceData) {
                for (const u of group) {
                    const entry = instMap[String(u.instanceIndex)];
                    if (entry?.status === 'failed' && entry.fail_reason) {
                        init[u.unitKey] = {
                            reason: entry.fail_reason,
                            action: entry.fail_action,
                            note:   entry.fail_note || null,
                        };
                    }
                }
            } else {
                const first = group[0];
                if (!first) continue;
                if (ps.pack_status === 'failed' && ps.pack_fail_reason) {
                    init[first.unitKey] = {
                        reason: ps.pack_fail_reason,
                        action: ps.pack_fail_action,
                        note:   null,  // denorm columns don't carry the note; pack_instances path above is preferred
                    };
                }
            }
        }
        return init;
    });

    const [detailKey, setDetailKey]     = useState(null);
    const [failModal, setFailModal]     = useState(null);
    const [reasons, setReasons]         = useState([]);
    const [sortMode, setSortMode]       = useState('product');
    const [activeDragKey, setActiveDragKey] = useState(null);
    const [portalState, setPortalState] = useState(savedPackState);
    const [moveOrder, setMoveOrder]     = useState([]);
    const [numberPreviews, setNumberPreviews] = useState({});
    const [scanInput, setScanInput]     = useState('');
    const [overScanModal, setOverScanModal] = useState(null); // { kind:'over'|'not_in_order', title, variant, sku, barcode, totalCount }
    const [flash, setFlash]             = useState(null);     // 'red' | null
    // Manager Override — order-wide, 60s window. Same pattern as picking/pressing.
    const MANAGER_OVERRIDE_MS = 60 * 1000;
    const [pendingMgrBarcode, setPendingMgrBarcode] = useState(null);
    const [managerOverride, setManagerOverride]     = useState(null);
    const [cameraOpen, setCameraOpen]               = useState(false);
    // 2026-09-25 (Eric): a scan that arrives while a bundle's detail is open and
    // undecided is HELD here — the packer passes or fails the open item first,
    // then the held scan is processed exactly as if it had just happened.
    // Before this, the second scan simply replaced the first bundle on screen
    // and left it unmarked.
    const [heldScan, setHeldScan]                   = useState(null); // { unit }
    const [_mgrNow, _setMgrNow]                     = useState(() => Date.now());
    useEffect(() => {
        if (!managerOverride) return;
        const id = setInterval(() => _setMgrNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [managerOverride]);
    useEffect(() => {
        if (managerOverride && _mgrNow > managerOverride.expiresAt) setManagerOverride(null);
    }, [_mgrNow, managerOverride]);
    const managerActive = !!(managerOverride && _mgrNow < managerOverride.expiresAt);
    useEffect(() => {
        setManagerOverride(null);
        setPendingMgrBarcode(null);
    }, [orderId]);
    const scanRef                       = useRef(null);
    const triggerFlash = useCallback((color) => {
        setFlash(color);
        setTimeout(() => setFlash(null), 600);
    }, []);
    // Units the user has actually touched in THIS session (pass / fail / reset via
    // the card or drag-and-drop). saveState() only writes back touched units so an
    // admin 'Reset Packing History' isn't clobbered by stale initial-state writes
    // for units the user didn't interact with this session.
    const touchedRef = useRef(new Set());
    // H10: surfaces when a QA save POST failed so the marks aren't silently lost.
    const [saveFailed, setSaveFailed] = useState(false);
    // unitKey -> 'scan' | 'manual'. Records how the user opened the unit so
    // saveState() can stamp payload.method on pack_pass / pack_fail events.
    const methodByKeyRef = useRef({});
    const openDetail = useCallback((unitKey, method) => {
        methodByKeyRef.current[unitKey] = method || 'manual';
        setDetailKey(unitKey);
    }, []);

    // Scan-only gate: packer flag + per-SKU require_scanning lookup.
    // Block the READY-column card click (manual pack_pass path) for scan_only_packer
    // users when the item's base SKU requires scanning. pack_fail (drag-to-rework)
    // is intentionally NOT gated — the fail path must always be available manually.
    const userScanOnlyPacker = useMemo(() => {
        // Shared PIN-login writes to 'presser_session' for all portal types (Pressing/Packing/Picking).
        // QCPortal does not migrate to a 'packer_session' key — reading from the wrong key
        // silently disables the scan-only gate.
        try {
            const s = JSON.parse(sessionStorage.getItem('presser_session') || 'null');
            return !!(s?.scan_only_packer);
        } catch { return false; }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // TP number overrides keyed by player name — shared with every QC card
    // and the DragOverlay so the RDS number wins over the checkout-embedded
    // one for clubs on the override list. Yellow-highlighted #-badge shows
    // when a swap occurred (see QCBundleCard).
    const tpOverridesByName = useMemo(() =>
        ((order?.tp_number_overrides) || []).reduce((m, ov) => {
            if (ov && ov.player_name) m[ov.player_name] = ov;
            return m;
        }, {}),
    [order?.tp_number_overrides]);

    const requireScanningBySku = useMemo(
        () => order?._cache?.require_scanning_by_sku || {},
        [order]
    );

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    );

    useEffect(() => {
        fetchFn('/api/packing/settings')
            .then(r => r.json())
            .then(d => setReasons((d.qa_fail_reasons || []).filter(r => !r.archived).map(r => r.text ?? r)))
            .catch(() => {});
    }, [fetchFn]);

    useEffect(() => {
        if (!orderId) return;
        fetchFn(`/api/orders/${orderId}/number-previews`)
            .then(r => r.ok ? r.json() : { previews: {} })
            .then(d => setNumberPreviews(d.previews || {}))
            .catch(() => setNumberPreviews({}));
    }, [orderId, fetchFn]);

    const identity = useMemo(() => ({
        order_name:          order?.replacement_alias || order?.name,
        shopify_location_id: session.shopify_location_id,
        portal_userid:       session.presser_id,
        portal_username:     `${session.first_name} ${session.last_name}`,
    }), [order, session]);

    const recordQA = useCallback((unit, result, reason = null, action = null, note = null) => {
        touchedRef.current.add(unit.unitKey);
        if (result === 'pass') {
            playSuccess();
            setColumnByKey(prev => ({ ...prev, [unit.unitKey]: 'passed' }));
            setFailState(prev => { const n = { ...prev }; delete n[unit.unitKey]; return n; });
            setMoveOrder(prev => [unit.unitKey, ...prev.filter(k => k !== unit.unitKey)]);
            setPortalState(prev => {
                const next = { ...prev };
                const group = groupsByGid[unit.gid];
                if (group) {
                    for (const li of group.items) {
                        if (li.sku) {
                            next[li.sku] = {
                                ...prev[li.sku],
                                qty_picked:  Math.max(prev[li.sku]?.qty_picked || 0, unit.instanceIndex),
                                qty_pressed: Math.max(prev[li.sku]?.qty_pressed || 0, unit.instanceIndex),
                            };
                        }
                    }
                }
                next[unit.group_key] = { ...prev[unit.group_key], pack_status: 'passed' };
                return next;
            });
        } else if (result === 'fail') {
            playError();
            setColumnByKey(prev => ({ ...prev, [unit.unitKey]: 'rework' }));
            setFailState(prev => ({ ...prev, [unit.unitKey]: { reason, action: 'rework', note: note || null } }));
            setMoveOrder(prev => [unit.unitKey, ...prev.filter(k => k !== unit.unitKey)]);
        } else if (result === 'reset') {
            setColumnByKey(prev => ({ ...prev, [unit.unitKey]: 'ready' }));
            setFailState(prev => { const n = { ...prev }; delete n[unit.unitKey]; return n; });
            setMoveOrder(prev => [unit.unitKey, ...prev.filter(k => k !== unit.unitKey)]);
        }

        setDetailKey(null);
        setFailModal(null);
        window.scrollTo({ top: 0 });
        requestAnimationFrame(() => window.scrollTo({ top: 0 }));
    }, [groupsByGid]);

    const saveState = useCallback(async () => {
        // H10: track whether any QA POST failed (network error OR non-2xx) so we
        // never clear the touched set — and never report success — on a failed
        // save. Silently dropping a QA pass/fail record is real data loss.
        let anyFailed = false;
        // qty_total per SKU = total ordered quantity across all line items of
        // that SKU. Needed so the backend INSERT has a non-zero total (picked %
        // divides by qty_total).
        const skuTotalBySku = {};
        for (const e of (order?.lineItems?.edges || [])) {
            const li = e.node;
            if (!li?.sku) continue;
            skuTotalBySku[li.sku] = (skuTotalBySku[li.sku] || 0) + (li.quantity || 0);
        }

        const promises = units.map(unit => {
            // Skip units the user never touched in this session — avoids re-writing
            // stale initial-state pass/fail marks after an admin Reset Packing History.
            if (!touchedRef.current.has(unit.unitKey)) return null;
            const col = columnByKey[unit.unitKey];
            if (col === 'ready') return null;
            const fs = failState[unit.unitKey];
            const result = col === 'passed' ? 'pass' : 'fail';
            const _method = methodByKeyRef.current[unit.unitKey] || 'manual';
            const payload = {
                group_key:       unit.group_key,
                instance_index:  unit.instanceIndex,
                result,
                fail_reason:     fs?.reason || null,
                fail_action:     col === 'rework' ? 'rework' : null,
                fail_note:       fs?.note || null,
                method:          _method,
                event_source:    result === 'pass' ? _method : 'manual',
                ...identity,
                ...(managerActive && managerOverride ? {
                    manager_override: {
                        user_id:    managerOverride.manager_user_id,
                        first_name: managerOverride.first_name,
                        last_name:  managerOverride.last_name,
                        name:       managerOverride.manager_name,
                    },
                } : {}),
            };
            if (result === 'pass' || result === 'fail') {
                const group = groupsByGid[unit.gid];
                if (group) {
                    // Per-QA contribution: every SKU in the bundle adds +1 to
                    // qty_picked. For BUNDLES (parent + embellishments) every SKU
                    // also adds +1 to qty_pressed — the full bundle went through
                    // press. For STANDALONE items (no embellishments) qty_pressed
                    // stays 0 since a lone sock/cleat isn't a press target.
                    // Sent on BOTH pass and fail — a reworked unit was still
                    // physically picked and pressed before the QC rejection.
                    // Backend gates on prior-status so pass→fail or fail→pass
                    // transitions don't double-count.
                    const isBundle = group.items.length > 1;
                    const pickItems = group.items
                        .map(li => ({
                            group_key:   li.sku || ('__no_sku__' + li.id),
                            qty_total:   li.sku ? (skuTotalBySku[li.sku] || 0) : (li.quantity || 1),
                            qty_picked:  1,
                            qty_pressed: isBundle ? 1 : 0,
                        }));
                    if (pickItems.length > 0) payload.pick_press_items = pickItems;
                }
            }
            // Deferred: a thunk, not a live request — saves are sent ONE AT A
            // TIME below. Firing them all at once (Promise.all) made concurrent
            // /qa transactions for the same order deadlock in Postgres (~12/day,
            // 2026-09-02), each losing that unit's QC mark as a 500.
            return () => fetchFn(`/api/packing/orders/${orderId}/qa`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            }, session.store_key)
                .then(res => { if (!res || res.ok === false) anyFailed = true; return res; })
                .catch(() => { anyFailed = true; });
        }).filter(Boolean);

        // For every touched unit currently back in Ready, always POST a reset —
        // covers both (a) items that were passed in a prior session and are being
        // moved back this session, and (b) items passed earlier this session
        // (already POSTed) that the user then moved back to Ready before close.
        // Reset is idempotent on the server for already-pending rows.
        //
        // We ship pick_press_items with the same shape as pass/fail so the backend
        // can REVERT the cascade: if the prior status was passed/failed, the
        // backend decrements qty_picked/qty_pressed by these amounts (floored at
        // 0). If the row is already pending on the server, nothing to revert.
        for (const unit of units) {
            if (!touchedRef.current.has(unit.unitKey)) continue;
            if (columnByKey[unit.unitKey] !== 'ready') continue;
            const resetPayload = {
                group_key:       unit.group_key,
                instance_index:  unit.instanceIndex,
                result:          'reset',
                ...identity,
            };
            const group = groupsByGid[unit.gid];
            if (group) {
                const isBundle = group.items.length > 1;
                const pickItems = group.items
                    .map(li => ({
                        group_key:   li.sku || ('__no_sku__' + li.id),
                        qty_total:   li.sku ? (skuTotalBySku[li.sku] || 0) : (li.quantity || 1),
                        qty_picked:  1,
                        qty_pressed: isBundle ? 1 : 0,
                    }));
                if (pickItems.length > 0) resetPayload.pick_press_items = pickItems;
            }
            promises.push(() =>
                fetchFn(`/api/packing/orders/${orderId}/qa`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(resetPayload),
                }, session.store_key)
                    .then(res => { if (!res || res.ok === false) anyFailed = true; return res; })
                    .catch(() => { anyFailed = true; })
            );
        }

        for (const run of promises) { await run(); }   // sequential, see note above
        if (anyFailed) {
            // H10: a POST failed — KEEP the touched set so a retry / the close
            // button re-sends these QA marks, and report failure so callers
            // don't exit as if the save succeeded.
            setSaveFailed(true);
            return false;
        }
        setSaveFailed(false);
        // Clear the touched set after a successful save. Without this, a second
        // saveState() (e.g. triggered by the close button after splitAndSlip
        // already saved + transferred the passed units) re-POSTs pass for units
        // whose FO is gone, which the backend happily writes into pack_instances,
        // undoing the reconcile-on-transfer reset. recordQA re-adds on any new
        // drag so future touches still persist.
        touchedRef.current = new Set();
        return true;
    }, [units, columnByKey, failState, identity, groupsByGid, fetchFn, orderId, session.store_key, savedPackState]);

    const handleDragEnd = useCallback((event) => {
        setActiveDragKey(null);
        const { active, over } = event;
        if (!over) return;
        const unitKey = active.id;
        const target  = over.id;
        const fromCol = columnByKey[unitKey];
        if (target === fromCol) return;
        if (fromCol === 'ready') return;

        const unit = units.find(u => u.unitKey === unitKey);
        if (!unit) return;

        if (target === 'rework') {
            setFailModal({ unitKey, preselectedAction: 'rework' });
        } else if (target === 'passed') {
            recordQA(unit, 'pass');
        } else if (target === 'ready') {
            recordQA(unit, 'reset');
        }
    }, [columnByKey, units, recordQA]);

    const bundleStates = useMemo(() => {
        const stateMap = {};
        for (const u of units) {
            const group = groupsByGid[u.gid];
            if (!group) continue;
            const parent = group.items.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || group.items[0];
            const parentSku = parent?.sku || '';
            const ps = portalState[parentSku];
            const picked = ps?.qty_picked !== undefined && ps.qty_picked !== null ? ps.qty_picked >= u.instanceIndex : false;
            // Pressed flag: prefer the per-instance press_instances map. Falls
            // back to the legacy `qty_pressed >= instanceIndex` comparison only
            // when the map is empty (transition window for legacy data). The
            // scalar form was wrong when bundles were pressed out of order —
            // same bug pattern as Pressing Portal / OrderDetail summaries.
            const pressInstances = ps?.press_instances || {};
            const hasPressMap    = Object.keys(pressInstances).length > 0;
            const pressed = hasPressMap
                ? !!pressInstances[String(u.instanceIndex)]?.pressed
                : (ps?.qty_pressed !== undefined && ps.qty_pressed !== null
                   ? ps.qty_pressed >= u.instanceIndex : false);
            stateMap[u.unitKey] = { picked, pressed };
        }
        return stateMap;
    }, [units, groupsByGid, portalState]);

    const getPickStatus = (unit) => {
        const s = bundleStates[unit.unitKey];
        if (!s) return [];
        const out = [];
        if (!s.picked) out.push('not-picked');
        if (!s.pressed && unit.isBundle) out.push('not-pressed');
        return out;
    };

    const sortedUnits = useMemo(() => {
        return [...units].sort((a, b) => {
            if (sortMode === 'player') {
                return (a.playerName || '').localeCompare(b.playerName || '');
            }
            return (a.productType || '').localeCompare(b.productType || '');
        });
    }, [units, sortMode]);

    const byMoveOrder = (a, b) => {
        const ai = moveOrder.indexOf(a.unitKey);
        const bi = moveOrder.indexOf(b.unitKey);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    };
    const readyUnits    = sortedUnits.filter(u => columnByKey[u.unitKey] === 'ready').sort(byMoveOrder);
    const passedUnits   = sortedUnits.filter(u => columnByKey[u.unitKey] === 'passed').sort(byMoveOrder);
    const reworkUnits   = sortedUnits.filter(u => columnByKey[u.unitKey] === 'rework').sort(byMoveOrder);

    // FO id at the operator's location — used by the rework slip print button
    // so the slip is correctly scoped when an order has FOs at multiple
    // locations. Derived once from the order detail; falls back to the first
    // FO if no location match (shouldn't happen in practice since this whole
    // board is scoped to one location, but defensive).
    const reworkPrintFoId = useMemo(() => {
        const edges = (order?.fulfillmentOrders?.edges || []).map(e => e.node);
        const match = edges.find(fo =>
            ((fo?.assignedLocation?.location?.id) || '').split('/').pop() === session.shopify_location_id
        );
        return (match || edges[0])?.id || '';
    }, [order, session.shopify_location_id]);

    const orderClub = getAttr(order?.customAttributes, 'Club', 'club', 'club_name', 'Team Club', 'team_club') || '';
    const allGroups = Object.values(groupsByGid);
    const teamNames   = [...new Set(allGroups.map(getGroupTeamName).filter(Boolean))];
    const playerNames = [...new Set(units.map(u => u.playerName).filter(Boolean))];

    const detailUnit  = detailKey ? units.find(u => u.unitKey === detailKey) : null;
    const detailGroup = detailUnit ? groupsByGid[detailUnit.gid] : null;

    const detailBundlePickState = useMemo(() => {
        if (!detailGroup) return null;
        const states = computeBundleStates([detailGroup], portalState);
        return states[0] || null;
    }, [detailGroup, portalState]);

    const failModalUnit = failModal ? units.find(u => u.unitKey === failModal.unitKey) : null;

    // Scan-bar: match scan against each bundle's parent barcode or sku.
    // Phase 3C: also matches when the scan equals the NEW SKU of an active
    // swap whose OLD SKU is the parent's SKU (so scanning a swapped-in
    // product credits the original bundle).
    const findAllMatchingGids = useCallback((raw) => {
        const q = (raw || '').trim().toLowerCase();
        if (!q) return [];
        return Object.keys(groupsByGid).filter(gid => {
            const g = groupsByGid[gid];
            const parent = g.items?.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || g.items?.[0];
            if (!parent) return false;
            const bc  = (parent.variant?.barcode || '').trim().toLowerCase();
            const sku = (parent.sku || '').trim().toLowerCase();
            if (bc && bc === q) return true;
            if (sku && sku === q) return true;
            return false;
        });
    }, [groupsByGid]);

    const handleScan = useCallback((raw) => {
        const q = (raw || '').trim();
        if (!q) return;
        if (heldScan) { setScanInput(''); return; }      // decide the open item first
        if (MANAGER_BARCODE_RX.test(q.toUpperCase())) {
            setScanInput('');
            setPendingMgrBarcode(q.toUpperCase());
            return;
        }
        const matchedGids = findAllMatchingGids(q);
        if (matchedGids.length === 0) {
            playError();
            triggerFlash('red');
            setScanInput('');
            setOverScanModal({ kind: 'not_in_order', barcode: q });
            return;
        }
        const matchedUnits = units.filter(u => matchedGids.includes(u.gid));
        const readyMatched = matchedUnits.filter(u => columnByKey[u.unitKey] === 'ready');
        if (readyMatched.length === 0) {
            const g = groupsByGid[matchedUnits[0].gid];
            const parent = g?.items?.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || g?.items?.[0];
            const vt = parent?.variant?.title;
            playError();
            triggerFlash('red');
            setScanInput('');
            setOverScanModal({
                kind:       'over',
                title:      parent?.title || t('Item'),
                variant:    (vt && vt !== 'Default Title') ? vt : null,
                sku:        parent?.sku || null,
                barcode:    parent?.variant?.barcode || q,
                totalCount: matchedUnits.length,
            });
            return;
        }
        const first = readyMatched[0];
        if (detailKey) {
            setScanInput('');
            if (first.unitKey === detailKey) return;          // rescan of the open item
            playSuccess();
            setHeldScan({ unit: first });                     // ask about the open one
            return;
        }
        playSuccess();
        // 2026-08-19: individual (non-bundle) items — socks, standalone
        // garments with no embellishment children — auto-pass QC on scan.
        // Rationale: a scanned barcode on a plain product is proof enough
        // that the right physical item was pulled; the intermediate
        // PASS QC / FAIL QC screen is friction for the vast majority of
        // scans. Bundles still open the detail view (packer needs to
        // eyeball embellishment quality before deciding pass/fail).
        // Manual tap still opens detail — the openDetail path is unchanged
        // for non-scan interactions.
        if (!first.isBundle) {
            recordQA(first, 'pass');
            setScanInput('');
            return;
        }
        openDetail(first.unitKey, 'scan');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setScanInput('');
    }, [findAllMatchingGids, units, columnByKey, groupsByGid, triggerFlash, openDetail, recordQA, detailKey, heldScan]);

    // The open item has been decided (recordQA clears detailKey) — now act on
    // the held scan the same way a fresh scan would: plain items auto-pass,
    // bundles open their detail. A held unit that is no longer READY (the
    // decision itself could not move it, but a concurrent packer can) is dropped.
    useEffect(() => {
        if (!heldScan || detailKey || failModal) return;
        const u = heldScan.unit;
        setHeldScan(null);
        if (columnByKey[u.unitKey] !== 'ready') return;
        if (!u.isBundle) { recordQA(u, 'pass'); return; }
        openDetail(u.unitKey, 'scan');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [heldScan, detailKey, failModal, columnByKey, recordQA, openDetail]);

    // Auto-focus scan input; refocus on blur (Bluetooth scanner support).
    useEffect(() => {
        if (detailKey || failModal || overScanModal) return;
        const el = scanRef.current;
        if (!el) return;
        el.focus();
        const onBlur = () => setTimeout(() => el.focus(), 80);
        el.addEventListener('blur', onBlur);
        return () => el.removeEventListener('blur', onBlur);
    }, [detailKey, failModal, overScanModal]);

    return (
        <div className={`qcb${managerActive ? ' qcb--mgr-active' : ''}`}>
            {managerActive && (
                <ManagerOverrideBanner expiresAt={managerOverride.expiresAt} now={_mgrNow} />
            )}
            <style>{buildFontFaceCss(numberPreviews)}</style>
            {saveFailed && (
                <div role="alert" style={{ background: '#FBEAE9', color: '#8B1A10', border: '1px solid #E0A9A3', borderRadius: 6, padding: '8px 12px', margin: '0 0 8px', fontWeight: 600 }}>
                    {t('Some QC marks could not be saved — they are kept, not lost. Check your connection and press Close & Save to retry.')}
                </div>
            )}
            <div className="ppb__hdr">
                <div className="ppb__hdr-left">
                    <span className="ppb__order">{order?.replacement_alias || order?.name}</span>
                    {orderClub && <span className="ppb__club">{orderClub}</span>}
                    {teamNames.length > 0 && <span className="ppb__team">{teamNames.join(' · ')}</span>}
                    {playerNames.length === 1 && <span className="ppb__players">{playerNames[0]}</span>}
                    {playerNames.length > 1 && (
                        <>
                            <span className="ppb__multi-players-badge">{t('Multiple Players')}</span>
                            <span className="ppb__players">{playerNames.join(' · ')}</span>
                        </>
                    )}
                </div>
                {notifications.length > 0 && onReviewNotifications && (
                    <button type="button" className="ppb__review-notif" onClick={onReviewNotifications}
                            title={t('Re-show this order\'s notification')}>
                        {t('Review Notification')}{notifications.length > 1 ? ` (${notifications.length})` : ''}
                    </button>
                )}
                <div className="ppb__hdr-right">
                    <div className="ppb__sort">
                        <button
                            className={`ppb__sort-btn${sortMode === 'product' ? ' ppb__sort-btn--active' : ''}`}
                            onClick={() => setSortMode('product')}
                        >{t('Product')}</button>
                        <button
                            className={`ppb__sort-btn${sortMode === 'player' ? ' ppb__sort-btn--active' : ''}`}
                            onClick={() => setSortMode('player')}
                        >{t('Player')}</button>
                    </div>
                    <button className="ppb__close-save" onClick={() => { saveState().then(ok => { if (ok) onExit(); }); }} title={t('Close & Save Order')}>{t('Close & Save Order')}</button>
                </div>
            </div>

            <DndContext
                sensors={sensors}
                onDragStart={(e) => setActiveDragKey(e.active.id)}
                onDragEnd={handleDragEnd}
            >
                <div className="qcb__cols">
                    {/*
                        Per-item height estimator for the virtualized kanban
                        columns (SP-297179 fix). Cards vary widely: front+back
                        images ≈ 420px, single image ≈ 300px, no images ≈ 170px,
                        plus warning-chip and player-name rows. A flat
                        estimateSize=220 undercounts 2-image cards by ~200px,
                        which triggers the @tanstack/react-virtual measurement-
                        propagation bug (see
                        reference_react_virtual_measurement_propagation) —
                        late-measured taller items land at estimate-based
                        translateY offsets and overlap each other. Per-item
                        estimate keeps initial positions close enough to actual
                        that measureElement settles cleanly with no overlap.
                    */}
                    <DroppableColumn
                        id="ready"
                        className="qcb__col qcb__col--ready"
                        title={t('READY FOR QC')}
                        count={readyUnits.length}
                        estimateSize={(idx) => qcCardHeightFor(readyUnits[idx])}
                        topAddon={
                            <div className="ppb__scan">
                                <input
                                    ref={scanRef}
                                    type="text"
                                    value={scanInput}
                                    onChange={e => setScanInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleScan(scanInput); } }}
                                    placeholder={t('Scan parent product barcode…')}
                                    autoFocus
                                    inputMode="none"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck={false}
                                    className="ppb__scan-input"
                                />
                                <button
                                    type="button"
                                    className="pkp-camera-btn"
                                    onClick={() => setCameraOpen(true)}
                                    title={t('Open camera scanner')}
                                >📷</button>
                            </div>
                        }
                        items={readyUnits}
                        getKey={(u) => u.unitKey}
                        renderItem={(u) => {
                            const baseSku = getBaseSku(u.sku || '');
                            const scanOnlyGated = userScanOnlyPacker && !!(baseSku && requireScanningBySku[baseSku]);
                            const manualBlocked = scanOnlyGated && !managerActive;
                            return (
                                <DraggableCard
                                    unitKey={u.unitKey}
                                    unit={u}
                                    pickStatus={getPickStatus(u)}
                                    onClick={manualBlocked ? undefined : () => { openDetail(u.unitKey, 'manual'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                    scanOnly={manualBlocked}
                                    overrideActive={scanOnlyGated && managerActive}
                                    skuNotification={u.sku ? skuNotifications[u.sku] : null}
                                    tpOverridesByName={tpOverridesByName}
                                />
                            );
                        }}
                    />
                    <DroppableColumn
                        id="passed"
                        className="qcb__col qcb__col--passed"
                        title={t('PASSED QC')}
                        count={passedUnits.length}
                        estimateSize={(idx) => qcCardHeightFor(passedUnits[idx])}
                        items={passedUnits}
                        getKey={(u) => u.unitKey}
                        renderItem={(u) => (
                            <DraggableCard unitKey={u.unitKey} unit={u}
                                skuNotification={u.sku ? skuNotifications[u.sku] : null}
                                tpOverridesByName={tpOverridesByName} />
                        )}
                    />
                    <DroppableColumn
                        id="rework"
                        className="qcb__col qcb__col--rework"
                        title={t('SEND FOR REWORK')}
                        count={reworkUnits.length}
                        estimateSize={(idx) => qcCardHeightFor(reworkUnits[idx])}
                        items={reworkUnits}
                        getKey={(u) => u.unitKey}
                        topAddon={reworkUnits.length > 0 ? (
                            <button
                                type="button"
                                className="qcb__col-rework-print-btn"
                                onClick={async () => {
                                    // Persist the kanban state first — the
                                    // rework-slip endpoint reads
                                    // item_state.pack_instances from the DB,
                                    // so without this the slip is empty until
                                    // the user manually saves. (Dragging cards
                                    // to the Rework column only mutates client
                                    // state; saveState() POSTs each touched
                                    // unit's QA to /api/packing/orders/{id}/qa.)
                                    try { await saveState(); } catch {}
                                    openReworkSlip({
                                        orderId,
                                        fulfillmentOrderId: reworkPrintFoId,
                                        storeKey: session.store_key,
                                        session,
                                    });
                                }}
                                title={t('Print rework slip for this order')}
                            >
                                🖨 {t('Print Rework Slip')}
                            </button>
                        ) : null}
                        renderItem={(u) => (
                            <DraggableCard
                                unitKey={u.unitKey}
                                unit={u}
                                failReason={failState[u.unitKey]?.reason}
                                skuNotification={u.sku ? skuNotifications[u.sku] : null}
                                tpOverridesByName={tpOverridesByName}
                            />
                        )}
                    />
                </div>
                <DragOverlay>
                    {activeDragKey && (() => {
                        const dragUnit = units.find(u => u.unitKey === activeDragKey);
                        return dragUnit ? <QCBundleCard unit={dragUnit} isDragging tpOverridesByName={tpOverridesByName} /> : null;
                    })()}
                </DragOverlay>
            </DndContext>

            <QCActionBar
                readyCount={readyUnits.length}
                reworkUnits={reworkUnits}
                passedUnits={passedUnits}
                virtualFoIds={virtualFoIds}
                orderId={orderId}
                orderInfo={order}
                session={session}
                storeKey={session.store_key}
                fetchFn={fetchFn}
                onSaveState={saveState}
                onDone={(action) => { saveState().then(ok => { if (ok) onExit(); }); }}
            />

            {detailUnit && detailGroup && (
                <QCBundleDetail
                    orderName={order?.replacement_alias || order?.name}
                    orderClub={orderClub}
                    unit={detailUnit}
                    group={detailGroup}
                    bundlePickState={detailBundlePickState}
                    numberPreviews={numberPreviews}
                    tpOverridesByName={
                        ((order?.tp_number_overrides) || []).reduce((m, ov) => {
                            if (ov && ov.player_name) m[ov.player_name] = ov;
                            return m;
                        }, {})
                    }
                    onBack={() => setDetailKey(null)}
                    onPass={() => recordQA(detailUnit, 'pass')}
                    onFail={() => setFailModal({ unitKey: detailUnit.unitKey, preselectedAction: null })}
                />
            )}

            {failModalUnit && (
                <QCFailModal
                    unit={failModalUnit}
                    reasons={reasons}
                    onSubmit={(reason, action, note) => recordQA(failModalUnit, 'fail', reason, action, note)}
                    onCancel={() => { setFailModal(null); setHeldScan(null); }}
                />
            )}

            {heldScan && detailUnit && !failModalUnit && (() => {
                const label = (u) => u.playerName
                    ? `${u.title} — ${u.playerName}${u.playerNumber ? ` #${u.playerNumber}` : ''}`
                    : u.title;
                return (
                    <div className="ppb__overscan-backdrop" data-testid="qcb-decide-first">
                        <div className="ppb__overscan-modal" onClick={e => e.stopPropagation()}>
                            <div className="ppb__overscan-title">{t('Pass or fail this item first')}</div>
                            <div className="ppb__overscan-body">
                                <strong>{label(detailUnit)}</strong> {t('has not been marked yet.')}
                                <div style={{ marginTop: 10, fontSize: 13, color: '#374151' }}>
                                    {t('You scanned')} <strong>{label(heldScan.unit)}</strong> — {t('it opens as soon as you decide.')}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                                <button className="qcb-detail-pass" style={{ flex: 1 }}
                                        onClick={() => recordQA(detailUnit, 'pass')}>
                                    ✓ {t('Pass QC')}
                                </button>
                                <button className="qcb-detail-fail" style={{ flex: 1 }}
                                        onClick={() => setFailModal({ unitKey: detailUnit.unitKey, preselectedAction: null })}>
                                    ✗ {t('Fail QC')}
                                </button>
                            </div>
                            <button className="ppb__overscan-ok" style={{ marginTop: 10, background: '#6b7280' }}
                                    onClick={() => setHeldScan(null)}>
                                {t('Stay on this item')}
                            </button>
                        </div>
                    </div>
                );
            })()}

            {overScanModal && (
                <div className="ppb__overscan-backdrop" onClick={() => setOverScanModal(null)}>
                    <div className="ppb__overscan-modal" onClick={e => e.stopPropagation()}>
                        {overScanModal.kind === 'not_in_order' ? (
                            <>
                                <div className="ppb__overscan-title">{t('Not in This Order')}</div>
                                <div className="ppb__overscan-body">
                                    {t('No bundle in this order matches the scanned barcode.')}
                                    <div className="ppb__overscan-barcode">{t('Barcode:')} {overScanModal.barcode}</div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="ppb__overscan-title">{t('Already QC\'d')}</div>
                                <div className="ppb__overscan-body">
                                    {overScanModal.totalCount === 1
                                        ? <>{t('This item has already been QC\'d and the order only contains one of it.')}</>
                                        : <>{t('All')} <strong>{overScanModal.totalCount}</strong> {t('of this item have already been QC\'d.')}</>}
                                    <div className="ppb__overscan-item">{overScanModal.title}</div>
                                    {overScanModal.variant && (
                                        <div className="ppb__overscan-variant">{overScanModal.variant}</div>
                                    )}
                                    {overScanModal.sku && (
                                        <div className="ppb__overscan-sku">SKU: {overScanModal.sku}</div>
                                    )}
                                    <div className="ppb__overscan-barcode">{t('Barcode:')} {overScanModal.barcode}</div>
                                </div>
                            </>
                        )}
                        <button
                            className="ppb__overscan-ok"
                            onClick={() => { setOverScanModal(null); setTimeout(() => scanRef.current?.focus(), 50); }}
                            autoFocus
                        >{t('OK')}</button>
                    </div>
                </div>
            )}

            {flash && ReactDOM.createPortal(
                <div className={`pkp-flash pkp-flash--${flash}`} />,
                document.body
            )}

            <ManagerOverrideModal
                open={!!pendingMgrBarcode}
                barcode={pendingMgrBarcode || ''}
                fetchFn={fetchFn}
                onClose={() => setPendingMgrBarcode(null)}
                onVerified={(mgr) => {
                    setManagerOverride({ ...mgr, expiresAt: Date.now() + MANAGER_OVERRIDE_MS });
                    setPendingMgrBarcode(null);
                }}
            />

            {cameraOpen && (
                <CameraScanner
                    onDetect={(bc) => {
                        setCameraOpen(false);
                        handleScan(bc);
                        setTimeout(() => scanRef.current?.focus(), 50);
                    }}
                    onClose={() => {
                        setCameraOpen(false);
                        setTimeout(() => scanRef.current?.focus(), 50);
                    }}
                />
            )}
        </div>
    );
}
